import { cloneDeep, isEqual, set } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Snackbar } from 'src/components/snackbar';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants';
import { useModal, useUpdateProjectConfigs } from 'src/hooks';
import {
  SCHEDULE_COMPONENT_INCLUDES,
  useGetComponentsWithIncludes,
} from 'src/hooks/api/components/useGetComponents';
import {
  DaypartGlobalComponentItem,
  useGetAllDayparts,
  useGetDaypartGlobalComponents,
  useUpdateDaypartGlobalComponents,
} from 'src/hooks/api/dayparts';
import { useGetProjectConfig } from 'src/hooks/api/projectConfig/useGetProjectConfigs';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { ComponentTypes } from 'src/interfaces';

import { RowItem } from '../modules/CustomerTriggeredEventsTable.interface';
import { IUpdateComponents } from '../modules/ScreenConfigSection.interface';
import {
  handleScreenSequence,
  sortedDaypartsByStartTime,
  sortRowsWithCODOnTop,
  transformRowsToPayload,
  validateTriggerConfigData,
} from './useCustomerTriggeredEventsPage.helper';

export interface CustomerTriggeredEventsErrors {
  rowId: string;
  daypartId: number;
  firstComponent: string;
  duplicateComponent: string;
}

export const useCustomerTriggeredEventsPage = () => {
  const { data: projectConfig, isFetched: isProjectConfigFetched } =
    useGetProjectConfig();
  const { data: dayparts = [], isFetched: isDaypartFetched } =
    useGetAllDayparts();
  const {
    data: daypartGlobalComponents = [],
    isFetched: isGlobalDaypartFetched,
  } = useGetDaypartGlobalComponents();
  const updateDaypartGlobalComponents = useUpdateDaypartGlobalComponents();
  const updateProjectConfigs = useUpdateProjectConfigs();
  const customerTriggeredEventsModal = useModal();
  const publicationModal = useModal();
  const [rows, setRows] = useState<RowItem[]>([]);

  const [globalComponentsScreensState, setGlobalComponentsScreensState] =
    useState(projectConfig?.globalComponentsScreens);

  const { data: components = [], isFetched: isComponentFetched } =
    useGetComponentsWithIncludes(
      daypartGlobalComponents.length > 0
        ? [
            `id.list=${daypartGlobalComponents
              .map((item) => item.component_id)
              .join('|')}`,
          ]
        : [],
      SCHEDULE_COMPONENT_INCLUDES,
      {
        enabled: daypartGlobalComponents.length > 0,
      },
    );

  const updateRows = useCallback(() => {
    const globalComponents = projectConfig?.globalComponents;

    if (
      !globalComponents ||
      !Array.isArray(globalComponents) ||
      globalComponents.length === 0
    )
      return;

    const sortedDayparts = sortedDaypartsByStartTime(dayparts);
    const componentDaypartMap = new Map<number, DaypartGlobalComponentItem[]>();
    daypartGlobalComponents.forEach((item) => {
      const existing = componentDaypartMap.get(item.component_id) || [];
      componentDaypartMap.set(item.component_id, [...existing, item]);
    });

    const transformedRows = globalComponents.map((item, index) => {
      const rowComponents = components
        .filter((comp) => {
          if (
            item.componentType === ComponentTypes.COD ||
            item.componentType === ComponentTypes.COD_THANK_YOU
          ) {
            return comp.component_type?.name === item.componentType;
          } else if (item.componentType === ComponentTypes.SINGLE_SHELF) {
            const isSingleShelf =
              comp.component_type?.name === ComponentTypes.SINGLE_SHELF;
            if (!isSingleShelf) {
              return false;
            }
            if (item.componentVariant) {
              const productSource = comp.config?.productsSource;
              const matches =
                productSource === item.componentVariant ||
                String(productSource) === String(item.componentVariant);

              return matches;
            }

            return false;
          }

          return false;
        })
        .filter((comp) => {
          const daypartIds = componentDaypartMap.get(comp.id) || [];

          return daypartIds.length > 0;
        })
        .map((comp, index) => {
          const daypartIds = componentDaypartMap.get(comp.id) || [];

          return {
            id: String(comp.id),
            name: comp.name,
            theme: comp.theme?.name,
            position: daypartIds?.[0]?.position || index + 1,
            selectedDayparts: daypartIds.filter((dp) =>
              sortedDayparts.some((d) => dp.daypart_id === d.id),
            ),
          };
        })
        .sort((a, b) => {
          return (a.position || 0) - (b.position || 0);
        });

      return {
        id: `r${index + 1}`,
        name: item.label || '',
        themeColor: item.color || '',
        expanded: rows?.[index]?.expanded ?? index === 0,
        components: rowComponents,
        componentType: item.componentType,
        componentVariant: item.componentVariant,
      };
    });

    const updatedRows = sortRowsWithCODOnTop(
      transformedRows,
      'Customer order display (COD)',
    );
    setRows(updatedRows);
  }, [
    components,
    daypartGlobalComponents,
    dayparts,
    projectConfig?.globalComponents,
    rows,
  ]);

  useEffect(() => {
    const isComponentConditionMet =
      daypartGlobalComponents.length === 0 || isComponentFetched;
    if (
      isGlobalDaypartFetched &&
      isProjectConfigFetched &&
      isDaypartFetched &&
      rows.length === 0 &&
      isComponentConditionMet
    ) {
      updateRows();
    }
  }, [
    projectConfig?.globalComponents,
    components,
    daypartGlobalComponents,
    isGlobalDaypartFetched,
    isComponentFetched,
    rows,
    updateRows,
    isProjectConfigFetched,
    isDaypartFetched,
  ]);

  const transformDaypartComponentsPayload = useCallback(
    () =>
      transformRowsToPayload(
        rows,
        daypartGlobalComponents,
        dayparts,
        components,
        projectConfig?.globalComponents ?? [],
      ),
    [
      components,
      daypartGlobalComponents,
      dayparts,
      projectConfig?.globalComponents,
      rows,
    ],
  );
  const validateScreenComponentsUpdated = useCallback(
    () =>
      !isEqual(
        globalComponentsScreensState,
        projectConfig?.globalComponentsScreens,
      ),
    [globalComponentsScreensState, projectConfig?.globalComponentsScreens],
  );

  const hasChanged = useMemo(
    () =>
      validateScreenComponentsUpdated() ||
      transformDaypartComponentsPayload().data.length > 0,
    [validateScreenComponentsUpdated, transformDaypartComponentsPayload],
  );

  const handleSaveConfirm = async (
    onSuccess: (isUpdated?: boolean) => void,
  ): Promise<void> => {
    const validTriggerData = validateTriggerConfigData(rows);
    if (!validTriggerData.valid) {
      return;
    }

    const payload = transformDaypartComponentsPayload();

    const isDaypartComponentsUpdated = payload.data.length > 0;
    const isScreenComponentsUpdated = validateScreenComponentsUpdated();

    try {
      if (isScreenComponentsUpdated) {
        await updateProjectConfigs.mutateAsync([
          {
            resource: COLLECTIONS.PROJECT_CONFIGS,
            method: 'PATCH',
            id: COLLECTIONS.GLOBAL_COMPONENTS_SCREENS,
            data: {
              value: globalComponentsScreensState,
              asset_names: null,
              narrative:
                narrativeGenerators[COLLECTIONS.GLOBAL_COMPONENTS_SCREENS][
                  USER_ACTIONS.UPDATE
                ](),
            },
          },
        ]);
      }

      // Make it under the isScreenComponentsUpdated to avoid a suddenly messaging change in the save modal
      if (isDaypartComponentsUpdated) {
        await updateDaypartGlobalComponents.mutateAsync(payload.data);
      } else {
        updateRows();
      }
    } catch (error: any) {
      Snackbar.error(error?.message);
    } finally {
      onSuccess(isDaypartComponentsUpdated);
    }
  };

  const handleGlobalComponentsScreensChange = (
    channelType: string,
    screenLayoutType: string,
    updateComponents: IUpdateComponents[],
    isSyncSequence?: boolean,
  ) => {
    setGlobalComponentsScreensState((prev) => {
      const next = cloneDeep(prev || {});
      const screenLayouts = next?.[channelType] || [];
      const screenLayoutIndex = next?.[channelType].findIndex(
        (s) => s.screenLayoutType === screenLayoutType,
      );
      const globalComponents =
        screenLayouts?.[screenLayoutIndex].globalComponents;

      for (const {
        componentType,
        componentVariant = '',
        updateValue,
      } of updateComponents) {
        const compIndex = globalComponents.findIndex(
          (gc) =>
            gc.componentType === componentType &&
            (gc?.componentVariant
              ? gc.componentVariant === componentVariant
              : true),
        );
        if (compIndex !== -1) {
          const prevValue = globalComponents?.[compIndex];

          const globalComponentsPath = `${channelType}[${screenLayoutIndex}].globalComponents`;

          if (updateValue.screenSequence) {
            handleScreenSequence(
              next,
              globalComponents,
              globalComponentsPath,
              {
                componentType,
                componentVariant,
                newScreenSequence: updateValue.screenSequence,
                prevScreenSequence: prevValue?.screenSequence,
              },
              isSyncSequence,
            );
          }

          set(next, `${globalComponentsPath}[${compIndex}]`, {
            ...prevValue,
            ...updateValue,
          });
        }
      }

      return next;
    });
  };

  const handleCancel = () => {
    setGlobalComponentsScreensState(projectConfig?.globalComponentsScreens);
    updateRows();
  };

  return {
    rows,
    setRows,
    handleSaveConfirm,
    customerTriggeredEventsModal,
    publicationModal,
    dayparts,
    handleCancel,
    projectConfig,
    globalComponentsScreensState,
    handleGlobalComponentsScreensChange,
    isSaveLoading:
      updateDaypartGlobalComponents.isLoading || updateProjectConfigs.isLoading,
    transformDaypartComponentsPayload,
    hasChanged,
  };
};
